import React from 'react';
import { useTheme } from 'next-themes';
import { Toaster as Sonner } from 'sonner';
import { CheckIcon } from 'lucide-react';

// NOTE: sonner's stylesheet is imported from the app's Tailwind entry
// (packages/web/app/tailwind.css), ahead of the Tailwind utilities, so the
// classNames below win the equal-specificity cascade against sonner's base
// styles (BB-3513). It is deliberately NOT imported here: a component-level
// import lands in a separate chunk whose order vs the utilities is not
// guaranteed, which would let sonner's base button/toast styles override these.

type ToasterProps = React.ComponentProps<typeof Sonner>;

export const Toaster = ({ ...props }: ToasterProps) => {
    const { theme = 'system' } = useTheme();

    return (
        <Sonner
            theme={theme as ToasterProps['theme']}
            className="toaster group"
            position="bottom-center"
            duration={5000}
            toastOptions={{
                style: {
                    gridTemplateColumns: '1.5rem 1fr auto auto',
                    // @ts-expect-error `We need a CSS variable here`
                    '--toast-button-margin-start': '0',
                },
                classNames: {
                    // `!border-0`: sonner's base `:where([data-sonner-toast][data-styled])`
                    // adds a 1px border. It's zero-specificity, so it ties with
                    // Tailwind's preflight `*{border-width:0}` and the winner depends on
                    // CSS source order (sonner wins in Vite dev → stray border). No other
                    // className touches the border, so force it off to match master (BB-3513).
                    toast: 'group toast group-[.toaster]:grid md:group-[.toaster]:min-h-22 group-[.toaster]:pl-7 md:group-[.toaster]:pl-14 group-[.toaster]:pr-5 group-[.toaster]:py-6 group-[.toaster]:!border-0 group-[.toaster]:bg-gray-100/85 group-[.toaster]:rounded-full group-[.toaster]:backdrop-blur group-[.toaster]:text-gray-900 group-[.toaster]:shadow-lg group-[.toaster]:justify-center',
                    title: 'group-[.toast]:text-sm md:group-[.toast]:text-lg group-[.toast]:font-normal',
                    description: 'group-[.toast]:text-muted-foreground',
                    icon: 'group-[.toast]:size-6',
                    // `!` important ONLY on the layout/shape props that sonner's base
                    // `[data-button]` rule also sets (radius, padding, height,
                    // font-size). Those selectors tie at specificity (0,3,0), so
                    // without important the winner depends on CSS source order, which
                    // is not stable across Vite dev vs prod (BB-3513). Colours
                    // (bg/text) are left non-important on purpose: individual toasts
                    // override them via per-toast `classNames` with `!important`
                    // (e.g. the design-board "View board" / "Dismiss" buttons), and
                    // marking them important here would collide with those overrides.
                    actionButton:
                        'group-[.toast]:justify-self-end group-[.toast]:!rounded-full group-[.toast]:justify-center group-[.toast]:!px-6 group-[.toast]:!py-2.5 group-[.toast]:bg-brick-50 group-[.toast]:text-primary-foreground group-[.toast]:!text-base group-[.toast]:font-medium group-[.toast]:tracking-normal group-[.toast]:!h-unset md:group-[.toast]:min-w-[12.5rem] group-[.toast]:hover:bg-brick-200',
                    cancelButton:
                        'group-[.toast]:justify-self-end group-[.toast]:!rounded-full group-[.toast]:justify-center group-[.toast]:!px-6 group-[.toast]:!py-2.5 group-[.toast]:bg-gray-200 group-[.toast]:text-gray-900 group-[.toast]:!text-base group-[.toast]:font-normal group-[.toast]:tracking-normal group-[.toast]:!h-unset md:group-[.toast]:min-w-[12.5rem] group-[.toast]:hover:bg-gray-300',
                },
            }}
            icons={{
                success: <CheckIcon className="size-6" strokeWidth={2} />,
            }}
            style={{
                // @ts-expect-error `We need a CSS variable here`
                '--width': 'min(56.125rem, 90dvw)',
                '--offset': '2.5rem',
            }}
            {...props}
        />
    );
};
